/**
 * Unauthenticated Fetch Helper
 *
 * Used for onboarding API calls that happen BEFORE the user is authenticated.
 * These calls don't use the standard auth layer (no session cookie, no auth headers).
 *
 * The onboarding flow is:
 * 1. User clicks magic link → token in URL
 * 2. Validate token → get user info (unauthenticated)
 * 3. Complete factor steps (unauthenticated - backend tracks via onboarding session)
 * 4. Exchange JWT for session cookie (becomes authenticated)
 */

import { getTenantFromPath } from '@/common/tenant-utils';

export interface FetchOptions extends Omit<RequestInit, 'body'> {
  body?: Record<string, unknown>;
}

/**
 * Convert raw backend error to user-friendly message
 * Handles technical error formats and returns clean, readable messages
 */
function getUserFriendlyErrorMessage(rawError: unknown): string {
  if (!rawError || typeof rawError !== 'string') {
    return 'Something went wrong. Please try again.';
  }

  const lowerError = rawError.toLowerCase();

  // Session/flow expired or not found
  if (
    lowerError.includes('not found or expired') ||
    lowerError.includes('expired') ||
    lowerError.includes('session')
  ) {
    return 'Your session has expired. Please refresh the page and try again.';
  }

  // Challenge failed state - cannot resend
  if (
    lowerError.includes('cannot resend') ||
    lowerError.includes('failed state') ||
    lowerError.includes('only pending')
  ) {
    return 'Too many failed attempts. Please refresh the page and start over.';
  }

  // Invalid code/verification
  if (
    lowerError.includes('invalid code') ||
    lowerError.includes('invalid verification') ||
    lowerError.includes('verification failed')
  ) {
    return 'Invalid code. Please try again.';
  }

  // Authentication errors
  if (lowerError.includes('unauthorized') || lowerError.includes('401')) {
    return 'Authentication failed. Please try again.';
  }

  // Rate limiting
  if (lowerError.includes('too many') || lowerError.includes('429')) {
    return 'Too many attempts. Please wait a moment and try again.';
  }

  // Network errors
  if (lowerError.includes('network') || lowerError.includes('fetch')) {
    return 'Connection error. Please check your internet and try again.';
  }

  // If it looks like a technical error (contains UUIDs, error codes, technical context), return generic message
  const technicalPatterns = [
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i, // UUID
    /code:\s*\w+_\d+/i, // Error codes like DATABASE_003, MFA_PUSH_003
    /entity_type=|entity_id=|challenge_id|validation_type/i, // Technical context
    /details=\{|context:/i, // Technical details
  ];

  if (technicalPatterns.some(pattern => pattern.test(rawError))) {
    return 'Something went wrong. Please try again.';
  }

  // Return the original if it seems user-friendly already
  return rawError;
}

/**
 * Fetch wrapper for unauthenticated API calls
 *
 * @param url - Full URL path (e.g., '/user-portal-api/onboarding/start')
 * @param options - Fetch options with typed body
 */
export async function unauthenticatedFetch<T>(
  url: string,
  options: FetchOptions = {}
): Promise<T> {
  const { body, headers, ...rest } = options;

  // Auto-prefix URL with tenant slug from path-based routing
  // e.g., /user-portal-api/onboarding/start → /acme/user-portal-api/onboarding/start
  const tenant = getTenantFromPath();
  const fullUrl = tenant ? `/${tenant}${url}` : url;

  const response = await fetch(fullUrl, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const rawMessage = error.detail ?? error.message;
    throw new Error(getUserFriendlyErrorMessage(rawMessage));
  }

  return response.json();
}
